// ---------------------------------------------------------------------------
// Repository: the single place that translates between SQLite rows and domain
// objects. Services and UI never touch SQL directly.
// ---------------------------------------------------------------------------

import { execute, query, queryOne, type Row } from './database';
import { newId, nowIso } from '../utils/id';
import {
  DEFAULT_SETTINGS,
  EMPTY_DOSSIER,
  type AppSettings,
  type AttemptVideoReview,
  type DentalProgram,
  type GeneratedAnswer,
  type GradingResult,
  type ImprovementPlanContent,
  type ImprovementPlanRecord,
  type InterviewQuestion,
  type MockInterview,
  type MockInterviewConfig,
  type MockInterviewQuestionPlan,
  type MockInterviewReport,
  type PracticeSession,
  type PrimaryApplicationMeta,
  type PromptAgent,
  type PromptTemplate,
  type PromptTemplateHistoryEntry,
  type RedFlagPlan,
  type ReviewState,
  type ReviewStatus,
  type SchoolDossier,
  type SavedAnswer,
  type SavedAnswerVersion,
  type ScoredAttempt,
  type SchoolFitProfile,
  type SessionMode,
  type StructuredProfile,
  type StudentProfile,
  type VoiceAnalytics,
} from '../types';

function num(v: unknown): number {
  return typeof v === 'number' ? v : Number(v ?? 0);
}
function str(v: unknown): string {
  return v == null ? '' : String(v);
}
function bool(v: unknown): boolean {
  return num(v) === 1;
}
function json<T>(v: unknown, fallback: T): T {
  if (v == null || v === '') return fallback;
  try {
    return JSON.parse(String(v)) as T;
  } catch {
    return fallback;
  }
}

// --- Settings --------------------------------------------------------------

export function getSettings(): AppSettings {
  const row = queryOne('SELECT data FROM app_settings WHERE id = 1');
  if (!row) return { ...DEFAULT_SETTINGS };
  const stored = json<Partial<AppSettings> & Record<string, unknown>>(row.data, {});
  // Pick only known keys so removed legacy settings (webSearchMode,
  // whisperModelPath, encryptLocalData, …) don't linger in memory or on save.
  const merged = { ...DEFAULT_SETTINGS };
  for (const key of Object.keys(DEFAULT_SETTINGS) as (keyof AppSettings)[]) {
    if (stored[key] !== undefined) {
      (merged as Record<string, unknown>)[key] = stored[key];
    }
  }
  // The app is Codex-only; force the provider regardless of any legacy stored value.
  merged.aiProvider = 'codex';
  return merged;
}

export function saveSettings(settings: AppSettings): void {
  execute('INSERT OR REPLACE INTO app_settings (id, data) VALUES (1, ?)', [
    JSON.stringify(settings),
  ]);
}

// --- Student profile -------------------------------------------------------

function mapProfile(row: Row): StudentProfile {
  return {
    id: str(row.id),
    name: str(row.name),
    inputs: json(row.inputs, {} as StudentProfile['inputs']),
    structured: json<StructuredProfile | null>(row.structured, null),
    createdAt: str(row.created_at),
    updatedAt: str(row.updated_at),
  };
}

export function getActiveProfile(): StudentProfile | null {
  const row = queryOne('SELECT * FROM student_profile ORDER BY updated_at DESC LIMIT 1');
  return row ? mapProfile(row) : null;
}

export function getProfileById(id: string): StudentProfile | null {
  const row = queryOne('SELECT * FROM student_profile WHERE id = ?', [id]);
  return row ? mapProfile(row) : null;
}

export function upsertProfile(profile: StudentProfile): StudentProfile {
  const existing = queryOne('SELECT id FROM student_profile WHERE id = ?', [profile.id]);
  const updatedAt = nowIso();
  if (existing) {
    execute(
      `UPDATE student_profile SET name = ?, inputs = ?, structured = ?, updated_at = ? WHERE id = ?`,
      [
        profile.name,
        JSON.stringify(profile.inputs),
        profile.structured ? JSON.stringify(profile.structured) : null,
        updatedAt,
        profile.id,
      ],
    );
  } else {
    execute(
      `INSERT INTO student_profile (id, name, inputs, structured, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?)`,
      [
        profile.id,
        profile.name,
        JSON.stringify(profile.inputs),
        profile.structured ? JSON.stringify(profile.structured) : null,
        profile.createdAt || updatedAt,
        updatedAt,
      ],
    );
  }
  return getProfileById(profile.id)!;
}

// --- Profile documents (primary application) --------------------------------
// The original file is stored so it can be re-downloaded; the UI only ever
// shows metadata, never the document body. One document per (profile, kind).

const PRIMARY_APPLICATION_KIND = 'primary_application';

export function savePrimaryApplication(
  profileId: string,
  filename: string,
  mime: string,
  bytes: Uint8Array,
  text: string,
): void {
  execute(
    `INSERT OR REPLACE INTO profile_document (profile_id, kind, filename, mime, data, text, created_at)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    [profileId, PRIMARY_APPLICATION_KIND, filename, mime, bytes, text, nowIso()],
  );
}

/** Metadata only (no blob) — what the Student Profile screen displays. */
export function getPrimaryApplicationMeta(profileId: string): PrimaryApplicationMeta | null {
  const row = queryOne(
    `SELECT profile_id, filename, mime, LENGTH(data) AS size_bytes, created_at
     FROM profile_document WHERE profile_id = ? AND kind = ?`,
    [profileId, PRIMARY_APPLICATION_KIND],
  );
  if (!row) return null;
  return {
    profileId: str(row.profile_id),
    filename: str(row.filename),
    mime: str(row.mime),
    sizeBytes: num(row.size_bytes),
    createdAt: str(row.created_at),
  };
}

/** The original uploaded file, for download. */
export function getPrimaryApplicationBytes(
  profileId: string,
): { filename: string; mime: string; bytes: Uint8Array } | null {
  const row = queryOne(
    'SELECT filename, mime, data FROM profile_document WHERE profile_id = ? AND kind = ?',
    [profileId, PRIMARY_APPLICATION_KIND],
  );
  if (!row || !(row.data instanceof Uint8Array)) return null;
  return { filename: str(row.filename), mime: str(row.mime), bytes: row.data };
}

/** The text extracted at upload time, for AI intake. */
export function getPrimaryApplicationText(profileId: string): string {
  const row = queryOne(
    'SELECT text FROM profile_document WHERE profile_id = ? AND kind = ?',
    [profileId, PRIMARY_APPLICATION_KIND],
  );
  return str(row?.text);
}

export function deletePrimaryApplication(profileId: string): void {
  execute('DELETE FROM profile_document WHERE profile_id = ? AND kind = ?', [
    profileId,
    PRIMARY_APPLICATION_KIND,
  ]);
}

// --- Dental programs -------------------------------------------------------

function mapProgram(row: Row): DentalProgram {
  return {
    id: str(row.id),
    name: str(row.name),
    shortName: str(row.short_name),
    fit: json(row.fit, {} as SchoolFitProfile),
    interviewDate: row.interview_date ? str(row.interview_date) : null,
    createdAt: str(row.created_at),
  };
}

export function listPrograms(): DentalProgram[] {
  return query('SELECT * FROM dental_program ORDER BY name').map(mapProgram);
}

export function getProgram(id: string): DentalProgram | null {
  const row = queryOne('SELECT * FROM dental_program WHERE id = ?', [id]);
  return row ? mapProgram(row) : null;
}

export function upsertProgram(program: DentalProgram): DentalProgram {
  const existing = queryOne('SELECT id FROM dental_program WHERE id = ?', [program.id]);
  if (existing) {
    execute(
      'UPDATE dental_program SET name = ?, short_name = ?, fit = ?, interview_date = ? WHERE id = ?',
      [
        program.name,
        program.shortName,
        JSON.stringify(program.fit),
        program.interviewDate ?? null,
        program.id,
      ],
    );
  } else {
    execute(
      'INSERT INTO dental_program (id, name, short_name, fit, interview_date, created_at) VALUES (?, ?, ?, ?, ?, ?)',
      [
        program.id,
        program.name,
        program.shortName,
        JSON.stringify(program.fit),
        program.interviewDate ?? null,
        program.createdAt || nowIso(),
      ],
    );
  }
  return getProgram(program.id)!;
}

/**
 * Update ONLY a program's school-fit profile, leaving every other column (most
 * importantly interview_date) untouched. Fit is refreshed by long-running
 * background AI tasks (research / fit build) that hold a snapshot of the program
 * captured when they started; upserting that whole snapshot would clobber an
 * interview date the user set while the task was running. This targeted write
 * closes that race. Returns the fresh program, or null if it was removed.
 */
export function updateProgramFit(id: string, fit: SchoolFitProfile): DentalProgram | null {
  execute('UPDATE dental_program SET fit = ? WHERE id = ?', [JSON.stringify(fit), id]);
  return getProgram(id);
}

export function setProgramInterviewDate(id: string, interviewDate: string | null): DentalProgram | null {
  execute('UPDATE dental_program SET interview_date = ? WHERE id = ?', [interviewDate, id]);
  return getProgram(id);
}

export function deleteProgram(id: string): void {
  execute('DELETE FROM dental_program WHERE id = ?', [id]);
}

// --- Interview questions ---------------------------------------------------

function mapQuestion(row: Row): InterviewQuestion {
  return {
    id: str(row.id),
    question: str(row.question),
    programId: row.program_id ? str(row.program_id) : null,
    category: str(row.category) as InterviewQuestion['category'],
    source: str(row.source),
    sourceUrl: str(row.source_url),
    sourceType: str(row.source_type) as InterviewQuestion['sourceType'],
    year: str(row.year),
    tags: json<string[]>(row.tags, []),
    confidence: str(row.confidence) as InterviewQuestion['confidence'],
    quality: str(row.quality) as InterviewQuestion['quality'],
    favorite: bool(row.favorite),
    practiced: bool(row.practiced),
    weak: bool(row.weak),
    hidden: bool(row.hidden),
    notes: str(row.notes),
    reviewStatus: (str(row.review_status) || 'accepted') as ReviewStatus,
    reviewedAt: row.reviewed_at ? str(row.reviewed_at) : null,
    reviewNotes: str(row.review_notes),
    createdAt: str(row.created_at),
  };
}

export interface QuestionFilter {
  programId?: string | null;
  category?: string;
  search?: string;
  favorite?: boolean;
  weak?: boolean;
  includeHidden?: boolean;
  reviewStatus?: ReviewStatus;
}

export function listQuestions(filter: QuestionFilter = {}): InterviewQuestion[] {
  const where: string[] = [];
  const params: (string | number)[] = [];
  if (!filter.includeHidden) where.push('hidden = 0');
  if (filter.programId !== undefined) {
    if (filter.programId === null) {
      where.push('program_id IS NULL');
    } else {
      where.push('program_id = ?');
      params.push(filter.programId);
    }
  }
  if (filter.category) {
    where.push('category = ?');
    params.push(filter.category);
  }
  if (filter.favorite) where.push('favorite = 1');
  if (filter.weak) where.push('weak = 1');
  if (filter.reviewStatus) {
    where.push('review_status = ?');
    params.push(filter.reviewStatus);
  }
  if (filter.search) {
    where.push('(LOWER(question) LIKE ? OR LOWER(notes) LIKE ? OR LOWER(tags) LIKE ?)');
    const like = `%${filter.search.toLowerCase()}%`;
    params.push(like, like, like);
  }
  const sql = `SELECT * FROM interview_question ${where.length ? 'WHERE ' + where.join(' AND ') : ''} ORDER BY created_at DESC`;
  return query(sql, params).map(mapQuestion);
}

export function getQuestion(id: string): InterviewQuestion | null {
  const row = queryOne('SELECT * FROM interview_question WHERE id = ?', [id]);
  return row ? mapQuestion(row) : null;
}

export function insertQuestion(
  q: Omit<InterviewQuestion, 'id' | 'createdAt' | 'reviewStatus' | 'reviewedAt' | 'reviewNotes'> &
    Partial<Pick<InterviewQuestion, 'id' | 'createdAt' | 'reviewStatus' | 'reviewedAt' | 'reviewNotes'>>,
): InterviewQuestion {
  const id = q.id ?? newId('q');
  execute(
    `INSERT INTO interview_question
     (id, question, program_id, category, source, source_url, source_type, year, tags, confidence, quality, favorite, practiced, weak, hidden, notes, review_status, reviewed_at, review_notes, created_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      q.question,
      q.programId ?? null,
      q.category,
      q.source ?? '',
      q.sourceUrl ?? '',
      q.sourceType ?? 'manual',
      q.year ?? '',
      JSON.stringify(q.tags ?? []),
      q.confidence ?? 'medium',
      q.quality ?? 'common',
      q.favorite ? 1 : 0,
      q.practiced ? 1 : 0,
      q.weak ? 1 : 0,
      q.hidden ? 1 : 0,
      q.notes ?? '',
      q.reviewStatus ?? 'accepted',
      q.reviewedAt ?? null,
      q.reviewNotes ?? '',
      q.createdAt ?? nowIso(),
    ],
  );
  return getQuestion(id)!;
}

export function updateQuestion(id: string, patch: Partial<InterviewQuestion>): void {
  const current = getQuestion(id);
  if (!current) return;
  const merged = { ...current, ...patch };
  execute(
    `UPDATE interview_question SET question = ?, program_id = ?, category = ?, source = ?, source_url = ?,
     source_type = ?, year = ?, tags = ?, confidence = ?, quality = ?, favorite = ?, practiced = ?, weak = ?, hidden = ?, notes = ?,
     review_status = ?, reviewed_at = ?, review_notes = ?
     WHERE id = ?`,
    [
      merged.question,
      merged.programId ?? null,
      merged.category,
      merged.source,
      merged.sourceUrl,
      merged.sourceType,
      merged.year,
      JSON.stringify(merged.tags),
      merged.confidence,
      merged.quality,
      merged.favorite ? 1 : 0,
      merged.practiced ? 1 : 0,
      merged.weak ? 1 : 0,
      merged.hidden ? 1 : 0,
      merged.notes,
      merged.reviewStatus,
      merged.reviewedAt ?? null,
      merged.reviewNotes,
      id,
    ],
  );
}

/** Set the research-review status for one question, stamping reviewed_at. */
export function setReviewStatus(id: string, status: ReviewStatus, notes?: string): void {
  const patch: Partial<InterviewQuestion> = { reviewStatus: status, reviewedAt: nowIso() };
  if (notes !== undefined) patch.reviewNotes = notes;
  // Rejected questions also leave the working set — same as hiding them.
  if (status === 'rejected') patch.hidden = true;
  updateQuestion(id, patch);
}

export function bulkSetReviewStatus(ids: string[], status: ReviewStatus): void {
  for (const id of ids) setReviewStatus(id, status);
}

export function countQuestionsNeedingReview(): number {
  const row = queryOne(
    "SELECT COUNT(*) AS c FROM interview_question WHERE hidden = 0 AND review_status = 'needs_review'",
  );
  return num(row?.c);
}

export function deleteQuestion(id: string): void {
  execute('DELETE FROM interview_question WHERE id = ?', [id]);
}

export function countQuestions(): number {
  const row = queryOne('SELECT COUNT(*) AS c FROM interview_question WHERE hidden = 0');
  return num(row?.c);
}

// --- Saved answers ---------------------------------------------------------

function mapAnswer(row: Row): SavedAnswer {
  return {
    id: str(row.id),
    questionId: str(row.question_id),
    generated: json<GeneratedAnswer | null>(row.generated, null),
    finalAnswer: str(row.final_answer),
    isBest: bool(row.is_best),
    notes: str(row.notes),
    createdAt: str(row.created_at),
    updatedAt: str(row.updated_at),
  };
}

export function listAnswersForQuestion(questionId: string): SavedAnswer[] {
  return query('SELECT * FROM saved_answer WHERE question_id = ? ORDER BY updated_at DESC', [
    questionId,
  ]).map(mapAnswer);
}

export function listAllAnswers(): SavedAnswer[] {
  return query('SELECT * FROM saved_answer ORDER BY updated_at DESC').map(mapAnswer);
}

export function getAnswer(id: string): SavedAnswer | null {
  const row = queryOne('SELECT * FROM saved_answer WHERE id = ?', [id]);
  return row ? mapAnswer(row) : null;
}

export function upsertAnswer(answer: SavedAnswer): SavedAnswer {
  const existing = queryOne('SELECT id, final_answer FROM saved_answer WHERE id = ?', [answer.id]);
  const updatedAt = nowIso();
  if (existing) {
    // Version history: keep the superseded final answer when the text changes.
    const previous = str(existing.final_answer);
    if (previous.trim() && previous !== answer.finalAnswer) {
      execute(
        'INSERT INTO saved_answer_version (id, answer_id, final_answer, saved_at) VALUES (?, ?, ?, ?)',
        [newId('ansv'), answer.id, previous, updatedAt],
      );
    }
    execute(
      `UPDATE saved_answer SET generated = ?, final_answer = ?, is_best = ?, notes = ?, updated_at = ? WHERE id = ?`,
      [
        answer.generated ? JSON.stringify(answer.generated) : null,
        answer.finalAnswer,
        answer.isBest ? 1 : 0,
        answer.notes,
        updatedAt,
        answer.id,
      ],
    );
  } else {
    execute(
      `INSERT INTO saved_answer (id, question_id, generated, final_answer, is_best, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        answer.id,
        answer.questionId,
        answer.generated ? JSON.stringify(answer.generated) : null,
        answer.finalAnswer,
        answer.isBest ? 1 : 0,
        answer.notes,
        answer.createdAt || updatedAt,
        updatedAt,
      ],
    );
  }
  return getAnswer(answer.id)!;
}

/** Previous final answers for one saved answer, newest first. */
export function listAnswerVersions(answerId: string): SavedAnswerVersion[] {
  return query(
    'SELECT * FROM saved_answer_version WHERE answer_id = ? ORDER BY saved_at DESC',
    [answerId],
  ).map((row) => ({
    id: str(row.id),
    answerId: str(row.answer_id),
    finalAnswer: str(row.final_answer),
    savedAt: str(row.saved_at),
  }));
}

export function setBestAnswer(questionId: string, answerId: string): void {
  execute('UPDATE saved_answer SET is_best = 0 WHERE question_id = ?', [questionId]);
  execute('UPDATE saved_answer SET is_best = 1 WHERE id = ?', [answerId]);
}

export function deleteAnswer(id: string): void {
  execute('DELETE FROM saved_answer WHERE id = ?', [id]);
}

// --- Practice sessions -----------------------------------------------------

export function createSession(mode: SessionMode): PracticeSession {
  const session: PracticeSession = { id: newId('sess'), mode, createdAt: nowIso() };
  execute('INSERT INTO practice_session (id, mode, created_at) VALUES (?, ?, ?)', [
    session.id,
    session.mode,
    session.createdAt,
  ]);
  return session;
}

// --- Practice attempts (graded answers) ------------------------------------

function mapAttempt(row: Row): ScoredAttempt {
  return {
    id: str(row.id),
    questionId: str(row.question_id),
    sessionId: row.session_id ? str(row.session_id) : null,
    mockInterviewId: row.mock_interview_id ? str(row.mock_interview_id) : null,
    transcriptText: str(row.transcript_text),
    normalizedHash: str(row.normalized_hash),
    rubricVersion: str(row.rubric_version),
    promptVersion: str(row.prompt_version),
    model: str(row.model),
    scores: json<Record<string, number>>(row.scores, {}),
    finalScore: num(row.final_score),
    grading: json<GradingResult>(row.grading, {} as GradingResult),
    analytics: json<VoiceAnalytics>(row.analytics, {} as VoiceAnalytics),
    cached: false,
    createdAt: str(row.created_at),
  };
}

export function findAttemptByHash(hash: string): ScoredAttempt | null {
  const row = queryOne(
    'SELECT * FROM practice_attempt WHERE normalized_hash = ? ORDER BY created_at DESC LIMIT 1',
    [hash],
  );
  return row ? mapAttempt(row) : null;
}

export function insertAttempt(attempt: ScoredAttempt): ScoredAttempt {
  execute(
    `INSERT INTO practice_attempt
     (id, question_id, session_id, mock_interview_id, transcript_text, normalized_hash, rubric_version, prompt_version, model, scores, final_score, grading, analytics, created_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      attempt.id,
      attempt.questionId,
      attempt.sessionId,
      attempt.mockInterviewId,
      attempt.transcriptText,
      attempt.normalizedHash,
      attempt.rubricVersion,
      attempt.promptVersion,
      attempt.model,
      JSON.stringify(attempt.scores),
      attempt.finalScore,
      JSON.stringify(attempt.grading),
      JSON.stringify(attempt.analytics),
      attempt.createdAt,
    ],
  );
  return attempt;
}

export function listAttempts(): ScoredAttempt[] {
  return query('SELECT * FROM practice_attempt ORDER BY created_at ASC').map(mapAttempt);
}

export function listAttemptsForQuestion(questionId: string): ScoredAttempt[] {
  return query(
    'SELECT * FROM practice_attempt WHERE question_id = ? ORDER BY created_at DESC',
    [questionId],
  ).map(mapAttempt);
}

export function listAttemptsForMock(mockId: string): ScoredAttempt[] {
  return query(
    'SELECT * FROM practice_attempt WHERE mock_interview_id = ? ORDER BY created_at ASC',
    [mockId],
  ).map(mapAttempt);
}

// --- Mock interviews -------------------------------------------------------

function mapMock(row: Row): MockInterview {
  return {
    id: str(row.id),
    config: json(row.config, {} as MockInterviewConfig),
    plan: json<MockInterviewQuestionPlan[]>(row.plan, []),
    attemptIds: json<string[]>(row.attempt_ids, []),
    report: json<MockInterviewReport | null>(row.report, null),
    status: str(row.status) as MockInterview['status'],
    currentStep: num(row.current_step),
    createdAt: str(row.created_at),
    completedAt: row.completed_at ? str(row.completed_at) : null,
  };
}

export function createMockInterview(
  config: MockInterviewConfig,
  plan: MockInterviewQuestionPlan[],
): MockInterview {
  const mock: MockInterview = {
    id: newId('mock'),
    config,
    plan,
    attemptIds: [],
    report: null,
    status: 'planned',
    currentStep: 0,
    createdAt: nowIso(),
    completedAt: null,
  };
  execute(
    `INSERT INTO mock_interview (id, config, plan, attempt_ids, report, status, current_step, created_at, completed_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      mock.id,
      JSON.stringify(mock.config),
      JSON.stringify(mock.plan),
      JSON.stringify(mock.attemptIds),
      null,
      mock.status,
      0,
      mock.createdAt,
      null,
    ],
  );
  return mock;
}

export function getMockInterview(id: string): MockInterview | null {
  const row = queryOne('SELECT * FROM mock_interview WHERE id = ?', [id]);
  return row ? mapMock(row) : null;
}

export function listMockInterviews(): MockInterview[] {
  return query('SELECT * FROM mock_interview ORDER BY created_at DESC').map(mapMock);
}

export function updateMockInterview(mock: MockInterview): void {
  execute(
    `UPDATE mock_interview SET config = ?, plan = ?, attempt_ids = ?, report = ?, status = ?, current_step = ?, completed_at = ? WHERE id = ?`,
    [
      JSON.stringify(mock.config),
      JSON.stringify(mock.plan),
      JSON.stringify(mock.attemptIds),
      mock.report ? JSON.stringify(mock.report) : null,
      mock.status,
      mock.currentStep,
      mock.completedAt,
      mock.id,
    ],
  );
}

/** Cheap progress write so an in-progress mock can resume after reload. */
export function setMockCurrentStep(id: string, step: number): void {
  execute('UPDATE mock_interview SET current_step = ? WHERE id = ?', [step, id]);
}

// --- Attempt audio (recorded answers) ---------------------------------------

export function saveAttemptAudio(attemptId: string, mime: string, bytes: Uint8Array): void {
  execute(
    'INSERT OR REPLACE INTO attempt_audio (attempt_id, mime, data, created_at) VALUES (?, ?, ?, ?)',
    [attemptId, mime, bytes, nowIso()],
  );
}

export function getAttemptAudio(attemptId: string): { mime: string; bytes: Uint8Array } | null {
  const row = queryOne('SELECT mime, data FROM attempt_audio WHERE attempt_id = ?', [attemptId]);
  if (!row || !(row.data instanceof Uint8Array)) return null;
  return { mime: str(row.mime), bytes: row.data };
}

// --- Attempt video (webcam recordings + manual self-review) ------------------

export function saveAttemptVideo(attemptId: string, mime: string, bytes: Uint8Array): void {
  execute(
    `INSERT OR REPLACE INTO attempt_video (attempt_id, mime, data, self_review, created_at)
     VALUES (?, ?, ?, COALESCE((SELECT self_review FROM attempt_video WHERE attempt_id = ?), ''), ?)`,
    [attemptId, mime, bytes, attemptId, nowIso()],
  );
}

export function getAttemptVideo(attemptId: string): { mime: string; bytes: Uint8Array } | null {
  const row = queryOne('SELECT mime, data FROM attempt_video WHERE attempt_id = ?', [attemptId]);
  if (!row || !(row.data instanceof Uint8Array)) return null;
  return { mime: str(row.mime), bytes: row.data };
}

/** Cheap existence check so lists can show a "video" chip without loading blobs. */
export function hasAttemptVideo(attemptId: string): boolean {
  return !!queryOne('SELECT attempt_id FROM attempt_video WHERE attempt_id = ?', [attemptId]);
}

export function getAttemptVideoReview(attemptId: string): AttemptVideoReview | null {
  const row = queryOne('SELECT self_review FROM attempt_video WHERE attempt_id = ?', [attemptId]);
  if (!row) return null;
  return json<AttemptVideoReview | null>(row.self_review, null);
}

export function saveAttemptVideoReview(attemptId: string, review: AttemptVideoReview): void {
  execute('UPDATE attempt_video SET self_review = ? WHERE attempt_id = ?', [
    JSON.stringify(review),
    attemptId,
  ]);
}

/** Videos are the biggest rows in the DB — let the user reclaim the space. */
export function deleteAttemptVideo(attemptId: string): void {
  execute('DELETE FROM attempt_video WHERE attempt_id = ?', [attemptId]);
}

// --- School dossiers ----------------------------------------------------------

export function getDossier(programId: string): SchoolDossier | null {
  const row = queryOne('SELECT data FROM program_dossier WHERE program_id = ?', [programId]);
  if (!row) return null;
  return { ...EMPTY_DOSSIER, ...json<Partial<SchoolDossier>>(row.data, {}) };
}

export function saveDossier(programId: string, dossier: SchoolDossier): void {
  execute(
    'INSERT OR REPLACE INTO program_dossier (program_id, data, updated_at) VALUES (?, ?, ?)',
    [programId, JSON.stringify(dossier), nowIso()],
  );
}

// --- Red-flag coaching plans ----------------------------------------------------

export function getRedFlagPlan(profileId: string): RedFlagPlan | null {
  const row = queryOne('SELECT data FROM red_flag_plan WHERE profile_id = ?', [profileId]);
  if (!row) return null;
  return json<RedFlagPlan | null>(row.data, null);
}

export function saveRedFlagPlan(profileId: string, plan: RedFlagPlan): void {
  execute('INSERT OR REPLACE INTO red_flag_plan (profile_id, data, created_at) VALUES (?, ?, ?)', [
    profileId,
    JSON.stringify(plan),
    nowIso(),
  ]);
}

// --- Improvement plans (persisted analytics output) --------------------------

function mapImprovementPlan(row: Row): ImprovementPlanRecord {
  return {
    id: str(row.id),
    plan: json<ImprovementPlanContent>(row.plan, {
      topWeaknesses: [], patterns: [], recommendedQuestionCategories: [], drills: [], summary: '',
    }),
    createdAt: str(row.created_at),
    sourceAttemptCount: num(row.source_attempt_count),
    baselineAverage: num(row.baseline_average),
    completedDrills: json<number[]>(row.completed_drills, []),
  };
}

export function insertImprovementPlan(
  plan: ImprovementPlanContent,
  sourceAttemptCount: number,
  baselineAverage: number,
): ImprovementPlanRecord {
  const record: ImprovementPlanRecord = {
    id: newId('plan'),
    plan,
    createdAt: nowIso(),
    sourceAttemptCount,
    baselineAverage,
    completedDrills: [],
  };
  execute(
    `INSERT INTO improvement_plan (id, plan, source_attempt_count, baseline_average, completed_drills, created_at)
     VALUES (?, ?, ?, ?, ?, ?)`,
    [record.id, JSON.stringify(plan), sourceAttemptCount, baselineAverage, '[]', record.createdAt],
  );
  return record;
}

export function listImprovementPlans(): ImprovementPlanRecord[] {
  return query('SELECT * FROM improvement_plan ORDER BY created_at DESC').map(mapImprovementPlan);
}

export function setImprovementPlanCompletedDrills(id: string, completedDrills: number[]): void {
  execute('UPDATE improvement_plan SET completed_drills = ? WHERE id = ?', [
    JSON.stringify(completedDrills),
    id,
  ]);
}

export function deleteImprovementPlan(id: string): void {
  execute('DELETE FROM improvement_plan WHERE id = ?', [id]);
}

// --- Spaced repetition (SM-2) -----------------------------------------------

function mapReview(row: Row): ReviewState {
  return {
    questionId: str(row.question_id),
    ease: num(row.ease),
    intervalDays: num(row.interval_days),
    repetitions: num(row.repetitions),
    dueAt: str(row.due_at),
    lastScore: num(row.last_score),
    updatedAt: str(row.updated_at),
  };
}

export function getReviewState(questionId: string): ReviewState | null {
  const row = queryOne('SELECT * FROM review_state WHERE question_id = ?', [questionId]);
  return row ? mapReview(row) : null;
}

/** Every question's SM-2 state, keyed by question — for review chips in lists. */
export function listReviewStates(): Map<string, ReviewState> {
  const map = new Map<string, ReviewState>();
  for (const row of query('SELECT * FROM review_state')) {
    const state = mapReview(row);
    map.set(state.questionId, state);
  }
  return map;
}

export function upsertReviewState(state: ReviewState): void {
  execute(
    `INSERT OR REPLACE INTO review_state
     (question_id, ease, interval_days, repetitions, due_at, last_score, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    [
      state.questionId,
      state.ease,
      state.intervalDays,
      state.repetitions,
      state.dueAt,
      state.lastScore,
      nowIso(),
    ],
  );
}

/** Questions whose scheduled review time has passed (excluding hidden ones). */
export function listDueQuestions(now: string = nowIso()): InterviewQuestion[] {
  return query(
    `SELECT q.* FROM interview_question q
     JOIN review_state r ON r.question_id = q.id
     WHERE q.hidden = 0 AND r.due_at <= ?
     ORDER BY r.due_at ASC`,
    [now],
  ).map(mapQuestion);
}

export function countDueQuestions(now: string = nowIso()): number {
  const row = queryOne(
    `SELECT COUNT(*) AS c FROM interview_question q
     JOIN review_state r ON r.question_id = q.id
     WHERE q.hidden = 0 AND r.due_at <= ?`,
    [now],
  );
  return num(row?.c);
}

// --- Prompt templates ------------------------------------------------------

function mapPrompt(row: Row): PromptTemplate {
  return {
    id: str(row.id),
    agent: str(row.agent) as PromptTemplate['agent'],
    version: str(row.version),
    label: str(row.label),
    template: str(row.template),
    updatedAt: str(row.updated_at),
  };
}

export function listPromptTemplates(): PromptTemplate[] {
  return query('SELECT * FROM prompt_template ORDER BY agent').map(mapPrompt);
}

/** The active (user-editable) template for one agent, or null if not seeded yet. */
export function getPromptTemplate(agent: PromptAgent): PromptTemplate | null {
  const row = queryOne('SELECT * FROM prompt_template WHERE agent = ? LIMIT 1', [agent]);
  return row ? mapPrompt(row) : null;
}

export function upsertPromptTemplate(tpl: PromptTemplate): void {
  const existing = queryOne('SELECT id, agent, version, template FROM prompt_template WHERE id = ?', [
    tpl.id,
  ]);
  if (existing) {
    // Keep the superseded body so edits can be audited and restored.
    if (str(existing.template) !== tpl.template) {
      execute(
        'INSERT INTO prompt_template_history (id, agent, version, template, saved_at) VALUES (?, ?, ?, ?, ?)',
        [newId('tplh'), str(existing.agent), str(existing.version), str(existing.template), nowIso()],
      );
    }
    execute(
      'UPDATE prompt_template SET agent = ?, version = ?, label = ?, template = ?, updated_at = ? WHERE id = ?',
      [tpl.agent, tpl.version, tpl.label, tpl.template, nowIso(), tpl.id],
    );
  } else {
    execute(
      'INSERT INTO prompt_template (id, agent, version, label, template, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
      [tpl.id, tpl.agent, tpl.version, tpl.label, tpl.template, nowIso()],
    );
  }
}

/** Superseded template bodies for one agent, newest first. */
export function listPromptTemplateHistory(agent: PromptAgent): PromptTemplateHistoryEntry[] {
  return query(
    'SELECT * FROM prompt_template_history WHERE agent = ? ORDER BY saved_at DESC',
    [agent],
  ).map((row) => ({
    id: str(row.id),
    agent: str(row.agent) as PromptAgent,
    version: str(row.version),
    template: str(row.template),
    savedAt: str(row.saved_at),
  }));
}

// --- Misc ------------------------------------------------------------------

export function isSeeded(): boolean {
  const row = queryOne("SELECT value FROM meta WHERE key = 'seeded'");
  return str(row?.value) === '1';
}

export function markSeeded(): void {
  execute("INSERT OR REPLACE INTO meta (key, value) VALUES ('seeded', '1')");
}
